import { useCallback, useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Loader2, ShieldX, ClockIcon } from "lucide-react";
import { DriverAuthProvider } from "@/context/DriverAuthContext";
import { useDriverAuth } from "../hooks/useDriverAuth";

// ---------------------------------------------------------------------------
// Inner guard — consumes the DriverAuthContext placed by the outer wrapper
// ---------------------------------------------------------------------------

function DriverAuthGuardInner() {
  const { driver, isAuthenticated, isLoading, signOut } = useDriverAuth();

  // Auto-sign-out on suspended/rejected so the stale session is cleared
  useEffect(() => {
    if (!isLoading && driver && (driver.status === "suspended" || driver.status === "rejected")) {
      // Do not sign out automatically — just block access and let the user see the message.
      // They can choose to sign out manually.
    }
  }, [isLoading, driver, signOut]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
          <p className="text-sm text-neutral-500">Chargement...</p>
        </div>
      </div>
    );
  }

  // ── Not authenticated ────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return <Navigate to="/livreur/connexion" replace />;
  }

  // ── Pending review ───────────────────────────────────────────────────────
  if (driver?.status === "pending_review") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 flex flex-col items-center gap-5 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
            <ClockIcon className="h-7 w-7 text-amber-500" />
          </div>
          <div className="space-y-2">
            <h2 className="text-base font-semibold text-neutral-900">
              Inscription en cours de vérification
            </h2>
            <p className="text-sm text-neutral-500 leading-relaxed">
              Votre dossier est en cours d'examen par notre équipe. Vous recevrez
              une notification dès que votre compte sera activé.
            </p>
          </div>
          <button
            type="button"
            onClick={signOut}
            className="mt-2 text-sm text-neutral-400 hover:text-neutral-600 transition-colors underline underline-offset-2"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  // ── Suspended or rejected ────────────────────────────────────────────────
  if (driver?.status === "suspended" || driver?.status === "rejected") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-neutral-100 p-8 flex flex-col items-center gap-5 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
            <ShieldX className="h-7 w-7 text-red-500" />
          </div>
          <div className="space-y-2">
            <h2 className="text-base font-semibold text-neutral-900">
              Accès suspendu
            </h2>
            <p className="text-sm text-neutral-500 leading-relaxed">
              Votre accès à ce portail a été suspendu. Veuillez contacter
              l'équipe CIEDA pour plus d'informations.
            </p>
          </div>
          <button
            type="button"
            onClick={signOut}
            className="mt-2 text-sm text-neutral-400 hover:text-neutral-600 transition-colors underline underline-offset-2"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  // ── Active driver — render protected routes ──────────────────────────────
  return <Outlet />;
}

// ---------------------------------------------------------------------------
// Outer wrapper — provides the context then renders the guard
// ---------------------------------------------------------------------------

/**
 * Route guard for all `/livreur/*` routes.
 *
 * Usage in the router:
 * ```tsx
 * <Route element={<DriverAuthGuard />}>
 *   <Route path="/livreur/tableau-de-bord" element={<DriverDashboard />} />
 * </Route>
 * ```
 */
export default function DriverAuthGuard() {
  return (
    <DriverAuthProvider>
      <DriverAuthGuardInner />
    </DriverAuthProvider>
  );
}
